import React, { useState } from 'react';
import { getFileName } from '../../utils/imageUtils';
import { getAssetNameFromUrl } from '../../utils/assetUtils';
import { useData } from '../../contexts/DataContext';
import ImageUpload from '../common/ImageUpload';
import VoiceInput from '../common/VoiceInput';
import AccordionSection from '../common/AccordionSection';

export default function LOTOSection({ permit, checklist, onUpdate, onLotoDeviation, isReadOnly, isGateMode, isOpen, onToggle, previewMode = false }) {
    const { adminData, locationData } = useData();
    const [deviationNote, setDeviationNote] = useState('');

    const risks = permit.risks || [];
    const lotoSteps = permit.lotoSteps || [];

    if (risks.length === 0 && lotoSteps.length === 0) return null;

    const firstIncompleteLotoIdx = lotoSteps.findIndex((_, idx) => !checklist[`loto_${idx}`]);
    const lotoAllDone = firstIncompleteLotoIdx === -1;
    const risksAllDone = risks.length === 0 || risks.every((_, idx) => checklist[`risk_${idx}`]);
    const sectionAllDone = lotoAllDone && risksAllDone;

    const activeGateStep = !lotoAllDone ? lotoSteps[firstIncompleteLotoIdx] : null;

    const handleCheck = (id) => {
        if (!isReadOnly) {
            onUpdate({ ...checklist, [id]: !checklist[id] });
        }
    };

    const handleGateCompletion = (url) => {
        onUpdate({
            ...checklist,
            [`loto_${firstIncompleteLotoIdx}`]: true,
            [`loto_photo_${firstIncompleteLotoIdx}`]: url,
            [`loto_note_${firstIncompleteLotoIdx}`]: deviationNote,
            [`loto_at_${firstIncompleteLotoIdx}`]: new Date().toISOString()
        });

        if (deviationNote && onLotoDeviation) {
            onLotoDeviation(url, deviationNote);
        }

        setDeviationNote(''); // Reset for next step
    };

    const getSmartLabel = (url) => {
        if (!url) return null;
        const contextHierarchy = adminData?.locations?.[permit.location]?.hierarchy ||
            (locationData?.name === permit.location ? locationData.hierarchy : null);

        return getAssetNameFromUrl(url, {
            ...permit,
            hierarchy: contextHierarchy || permit.hierarchy
        }) || getFileName(url);
    };

    // Calculate title
    let title = "VEILIGHEIDSMAATREGELEN & LOTO";
    if (!sectionAllDone && lotoSteps.length > 0) {
        title += ` (${firstIncompleteLotoIdx + 1}/${lotoSteps.length})`;
    }

    return (
        <AccordionSection
            title="VEILIGHEIDSMAATREGELEN & LOTO"
            icon="🛡️"
            isOpen={isOpen}
            isComplete={sectionAllDone}
            onClick={onToggle}
        >
            <div className="space-y-6 mt-4">

                {/* INFO CARD */}
                <div className="bg-slate-800/80 border border-slate-700 shadow-xl rounded-xl p-5 grid grid-cols-1 md:grid-cols-2 gap-6 ring-1 ring-white/5">
                    <div>
                        <div className="text-[10px] text-slate-500 uppercase font-black tracking-widest mb-1.5">Locatie & Taak</div>
                        <div className="font-bold text-white text-sm break-words">
                            {permit.hall}
                            {permit.line && <span className="text-slate-500 mx-2">/</span>}
                            {permit.line}
                        </div>
                        <div className="text-[11px] font-black text-blue-400 mt-1 uppercase tracking-tight">{permit.component} <span className="text-slate-600mx-1">›</span> {permit.task}</div>
                    </div>
                    <div>
                        <div className="text-[10px] text-slate-400 uppercase font-bold tracking-wider">Gecombineerd Risiconiveau</div>
                        {(() => {
                            let displayLevel = permit.riskLevel || 1;
                            if (permit.qualification === 'VP') displayLevel = Math.max(displayLevel, 3);
                            else if (permit.qualification === 'VOP') displayLevel = Math.max(displayLevel, 2);

                            return (
                                <div className="flex items-center gap-2 mt-1">
                                    <div className={`px-2 py-0.5 rounded text-[10px] font-black ${displayLevel > 1 ? 'bg-orange-500 text-black' : 'bg-green-500 text-black'
                                        }`}>
                                        NIVEAU {displayLevel}
                                    </div>
                                    <div className="text-[10px] text-slate-300">
                                        {permit.qualification && permit.qualification !== 'Geen'
                                            ? `Vereist: ${permit.qualification}`
                                            : 'Standaard'}
                                    </div>
                                </div>
                            );
                        })()}
                    </div>
                </div>

                {/* GENERAL HAZARDS (Risks) */}
                {risks.length > 0 && (
                    <div className="space-y-3">
                        <div className="text-[10px] text-slate-500 uppercase font-black tracking-widest pl-1">Situatieve Risico's (TRA)</div>
                        {risks.map((risk, idx) => {
                            const id = `risk_${idx}`;
                            const isChecked = !!checklist[id];
                            return (
                                <label
                                    key={idx}
                                    className={`flex items-start gap-4 p-4 md:p-5 rounded-xl border-2 transition-all shadow-lg cursor-pointer ${isChecked
                                        ? 'bg-green-500/10 border-green-500/30'
                                        : 'bg-slate-800/50 border-slate-700 hover:border-slate-600 hover:bg-slate-800'
                                        }`}
                                >
                                    <input
                                        type="checkbox"
                                        className="w-6 h-6 rounded border-slate-500 text-green-600 bg-slate-900 mt-1 cursor-pointer shrink-0"
                                        checked={isChecked}
                                        disabled={isReadOnly}
                                        onChange={() => handleCheck(id)}
                                    />
                                    <div className="flex-1">
                                        <div className={`font-bold text-sm leading-snug ${isChecked ? 'text-green-400' : 'text-slate-200'}`}>{risk.Risico}</div>
                                        <div className="text-xs text-slate-500 mt-1.5 italic font-medium leading-relaxed">{risk.Maatregel}</div>
                                    </div>
                                </label>
                            );
                        })}
                    </div>
                )}

                {/* LOTO STEPS */}
                {lotoSteps.length > 0 && (
                    <div className="space-y-4">
                        <div className="text-[10px] text-slate-500 uppercase font-black tracking-widest pl-1">Machine Isolatie (LOTO)</div>

                        {!lotoAllDone && isGateMode ? (
                            /* ACTIVE GATE STEP */
                            <div className="border-2 border-red-500/30 bg-red-950/10 rounded-xl overflow-hidden transition-all shadow-lg shadow-red-500/5">
                                <div className="p-3 bg-red-600/20 flex items-center gap-3">
                                    <div className="p-1.5 rounded-full text-red-400">
                                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                                        </svg>
                                    </div>
                                    <h4 className="font-bold text-sm text-white uppercase tracking-tight">BEWIJS FOTO VEREIST</h4>
                                </div>

                                <div className="p-4 md:p-5 space-y-4">
                                    <div className="bg-slate-800/80 p-4 rounded-lg border border-slate-700">
                                        <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest block mb-1">Actieve Stap</label>
                                        <div className="text-lg font-bold text-white mb-1 leading-tight">{activeGateStep.step || activeGateStep.type}</div>
                                        <div className="text-sm text-slate-300 leading-relaxed font-medium">
                                            {activeGateStep.instruction || activeGateStep.description}
                                        </div>
                                    </div>

                                    <div className="space-y-4">
                                        <div>
                                            <label className="block text-xs font-bold text-slate-400 mb-2 uppercase tracking-widest">Maak Bewijsfoto</label>
                                            <ImageUpload
                                                path="loto_evidence"
                                                selectedUrls={[]}
                                                onUploadComplete={(urls) => {
                                                    if (urls && urls.length > 0) {
                                                        const url = urls[urls.length - 1];
                                                        handleGateCompletion(url);
                                                    }
                                                }}
                                                compact={false}
                                                label="📸 FOTO MAKEN"
                                                className="w-full"
                                                btnClassName="py-4 text-base font-black bg-blue-600 hover:bg-blue-500 text-black border-none shadow-lg active:scale-95 transition-all uppercase"
                                                previewMode={previewMode}
                                            />
                                        </div>

                                        <div>
                                            <label className="block text-xs font-bold text-slate-400 mb-2 uppercase tracking-widest flex items-center gap-2">
                                                Melding afwijking <span className="text-[10px] lowercase font-normal opacity-70">(optioneel)</span>
                                            </label>
                                            <VoiceInput
                                                value={deviationNote}
                                                onChange={(e) => setDeviationNote(e.target.value)}
                                                placeholder="Stickers missen, nummering onjuist, of procedure wijkt af..."
                                                className="w-full bg-white border-2 border-slate-300 rounded-lg p-3 text-sm text-black font-medium focus:border-red-500 outline-none transition-all placeholder:text-slate-400 ring-4 ring-black/5"
                                                minHeight="h-16"
                                                label="🚨 Melding afwijking"
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ) : (
                            /* COMPLETED LIST OR READ-ONLY */
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {lotoSteps.map((step, idx) => {
                                    const id = `loto_${idx}`;
                                    const photoKey = `loto_photo_${idx}`;
                                    const isDone = checklist[id] || (permit.lotoPhotos && permit.lotoPhotos[idx]);
                                    const photoUrl = checklist[photoKey] || (permit.lotoPhotos && permit.lotoPhotos[idx]);
                                    const label = getSmartLabel(photoUrl);

                                    return (
                                        <div
                                            key={idx}
                                            className={`p-4 rounded-xl border-2 transition-all flex flex-col gap-3 shadow-lg ${isDone
                                                ? 'bg-green-900/10 border-green-500/20'
                                                : 'bg-slate-800/50 border-slate-700 opacity-50'
                                                }`}
                                        >
                                            <div className="flex items-start gap-4">
                                                <input
                                                    type="checkbox"
                                                    className="w-8 h-8 rounded-lg border-2 border-slate-600 bg-slate-950 text-green-500 cursor-pointer shrink-0 mt-0.5"
                                                    checked={!!isDone}
                                                    readOnly={true}
                                                />
                                                <div className="flex-1 min-w-0">
                                                    <div className={`font-bold text-sm ${isDone ? 'text-green-300' : 'text-slate-400'}`}>
                                                        {step.step || step.type}
                                                    </div>
                                                    <div className="text-xs text-slate-400 mt-1 line-clamp-2 italic">
                                                        {step.instruction || step.description}
                                                    </div>
                                                </div>
                                            </div>

                                            {photoUrl && (
                                                <div className="mt-2 rounded-lg overflow-hidden border border-slate-700 bg-black/40 shadow-inner">
                                                    <img src={photoUrl} alt="LOTO" className="w-full h-32 object-cover opacity-90" />
                                                    {label && (
                                                        <div className="px-2 py-1.5 text-[9px] bg-slate-800 border-t border-slate-700 text-slate-400 truncate font-black uppercase tracking-widest text-center">
                                                            {label}
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                )}
            </div>
        </AccordionSection>
    );
}
